import { useCart } from "@/hooks/useCart";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { useProductQuantity } from "@/hooks/useProductQuantity";
import { useAddToCart } from "@/hooks/useCartMutations";
import { toast, Toast } from "@/components/atoms/toast";
import { Locales } from "@/lib/i18n";
import { __, _x } from "@/lib/i18n/functions";
import { cn, formatPrice } from "@/lib/utils";
import { Minus, Plus, ShoppingCart } from "lucide-react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toLotQuantity,
  toPieceQuantity,
  toPiecePrice,
} from "@/lib/product-units";

const CollectionItem = ({
  productInfo,
  onQuantityChange,
  locale,
  isLoading,
  isProductsInCart,
}: {
  productInfo: {
    productName: string;
    productPrice: string;
    productQuantity: number;
    reference: string;
    variantId: string;
    medusaId: string;
    productConditioning: number | string;
    medusaPrice: number;
    isOutOfStock?: boolean;
  };
  isLoading: boolean;
  locale: Locales;
  onQuantityChange?: (quantity: number) => void;
  isProductsInCart: boolean;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSelected = searchParams.get("product") === productInfo.medusaId;
  const productConditioning = productInfo.productConditioning;
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const maximumQuantity = productInfo.productQuantity * quantityStep;

  const handleCardClick = () => {
    const params = new URLSearchParams(searchParams.toString());
    if (isSelected) {
      params.delete("product");
    } else {
      params.set("product", productInfo.medusaId);
    }
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };
  const [count, setCount] = useProductQuantity(productConditioning);
  const { cart } = useCart();
  const cartItem = cart?.items && cart?.items.find((item) => item.variant_id === productInfo.variantId);
  const isInCart = Boolean(cartItem);
  const cartQuantity = toLotQuantity(cartItem?.quantity || 0, productConditioning).toString();
  const [inputValue, setInputValue] = useState(toLotQuantity(count, productConditioning).toString());
  const [isEditing, setIsEditing] = useState(false);

  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      toast.success({ title: __("Item added to cart!", locale) });
    },
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  useEffect(() => {
    onQuantityChange?.(count);
  }, [count, onQuantityChange]);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(toLotQuantity(count, productConditioning).toString());
    }
  }, [count, isEditing, productConditioning]);

  const incrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (count < maximumQuantity) {
      setCount(count + quantityStep);
    }
  };
  const decrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (count > quantityStep) {
      setCount(count - quantityStep);
    }
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(productInfo.variantId, count);
  };

  const productName = productInfo.productName;

  const pricePerPiece = toPiecePrice(productInfo.medusaPrice, productConditioning);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty input or numbers only
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const numValue = parseInt(inputValue) || 1;
    const clampedValue = Math.min(maximumQuantity, toPieceQuantity(numValue, productConditioning));

    setCount(clampedValue);
    setInputValue(toLotQuantity(clampedValue, productConditioning).toString());
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setInputValue(toLotQuantity(count, productConditioning).toString());
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    setTimeout(() => {
      const input = document.activeElement as HTMLInputElement;
      if (input) input.select();
    }, 0);
  };

  return (
    <article className="relative flex cursor-pointer items-start justify-between gap-3 transition-all duration-200 ease-in-out hover:scale-[101%]">
      <button
        type="button"
        onClick={handleCardClick}
        aria-pressed={isSelected}
        className="absolute inset-0 z-10"
      >
        <span className="sr-only">{_x("View the product {{ productName }}", { productName }, locale)}</span>
      </button>

      {/* Product Name - flex-[4] to match header */}
      <div
        className={cn(
          "flex w-0 min-w-0 flex-[7] flex-col gap-1.5",
          isProductsInCart ? "min-[480px]:flex-[9] sm:flex-[4]" : "flex-[4]"
        )}
      >
        <p
          className={cn(
            "overflow-hidden text-sm text-wrap text-ellipsis whitespace-break-spaces",
            isSelected && "font-medium underline"
          )}
        >
          {productInfo.productName}
        </p>
        <div className="text-primary hidden w-max rounded-full bg-gray-100 px-2 py-1 text-xs md:block">
          {physicalPieceCount > 1
            ? formatProductQuantity(count, productConditioning, locale)
            : `${__("Per batch", locale)} (${count})`}
        </div>
        <span className="text-primary-light xs:text-sm text-[12px] md:hidden lg:block xl:hidden">
          {__("Ref", locale)}: {productInfo.reference}
        </span>
      </div>

      {/* Reference - flex-[2] to match header */}
      <span className="text-primary-light hidden flex-[2] text-sm md:block lg:hidden xl:block">
        {productInfo.reference}
      </span>

      {/* Price - flex-[2] to match header */}
      <div className="flex flex-[3] flex-col sm:flex-[2]">
        {isLoading ? <div className="shimmer h-8 w-12 rounded-md" /> : <span>{productInfo.productPrice}</span>}
        {physicalPieceCount > 1 && (
          <span className="xs:text-sm text-[12px]">
            {formatPrice(pricePerPiece, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            /{__("piece", locale)}
          </span>
        )}
      </div>

      {/* Quantity - flex-[3] to match header */}
      {isLoading && <div className="shimmer h-[30px] w-full flex-[3]"></div>}
      {productInfo.isOutOfStock ? (
        <Toast
          id={`col-out-${productInfo.variantId}`}
          type="info"
          title={`${__("Out of stock", locale)}`}
          hideIconOnSm
          className="min-[384px]:min-w-none w-fit max-w-none flex-[2.7] py-3 shadow-none"
        />
      ) : (
        !isLoading &&
        (!isInCart ? (
          <div className="z-20 flex h-[30px] min-w-[130px] flex-[3] items-center justify-between gap-1.25 rounded-sm">
            <button
              onClick={decrementCount}
              className="group flex size-[30px] cursor-pointer items-center justify-center rounded-full border"
              aria-label={__("Decrease Quantity", locale)}
              disabled={count <= quantityStep}
            >
              <Minus
                size={14}
                className={cn(
                  count <= quantityStep ? "text-primary-light/50" : "text-primary",
                  "group-focus:text-dark-primary"
                )}
                aria-hidden="true"
              />
            </button>
            <div className="flex h-full max-w-[65px] min-w-[40px] grow items-center justify-center rounded-md border">
              <input
                size={1}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onFocus={handleInputFocus}
                aria-live="polite"
                aria-atomic="true"
                aria-label={__("Quantity", locale)}
                className="w-full border-none bg-transparent text-center font-semibold outline-none focus:ring-0 md:text-base"
                min={1}
              />
            </div>
            <button
              onClick={incrementCount}
              className="group flex size-[30px] cursor-pointer items-center justify-center rounded-full border"
              aria-label={__("Increase Quantity", locale)}
              disabled={count >= maximumQuantity}
            >
              <Plus
                size={14}
                className={cn(
                  count >= maximumQuantity ? "text-primary-light/50" : "text-primary",
                  "group-focus:text-dark-primary"
                )}
                aria-hidden="true"
              />
            </button>
            {isAddingToCart ? (
              <div className="bg-pink-primary flex size-7 items-center justify-center rounded-md p-1">
                <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              </div>
            ) : (
              <button
                type="button"
                onClick={handleAddToCart}
                aria-label={_x("Add {{ quantity }} of {{ productName }} to cart", {
                  quantity: formatProductQuantity(count, productConditioning, locale), productName,
                }, locale)}
                className="bg-pink-primary size-7 shrink-0 cursor-pointer rounded-md p-1 text-white transition-opacity hover:opacity-90 active:scale-95"
              >
                <ShoppingCart aria-hidden="true" className="size-full" />
              </button>
            )}
          </div>
        ) : (
          <div
            className={cn(
              "flex min-w-[130px] flex-[3] items-center justify-center gap-2",
              isProductsInCart && "justify-end"
            )}
          >
            <ShoppingCart className="size-4 text-[#0093D0]" aria-hidden="true" />
            <span className="font-medium text-[#0093D0] max-sm:hidden">
              {_x("In cart ({{ cartQuantity }})", { cartQuantity }, locale)}
            </span>
            <span className="sm:hidden">{cartQuantity}</span>
          </div>
        ))
      )}
    </article>
  );
};

export default CollectionItem;
